import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ArrowLeft,
  Check,
  Loader2,
  RefreshCcw,
  Send,
  Sparkles,
  WandSparkles,
} from 'lucide-react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  aiMenuDesignApi,
  menusApi,
  menuTemplatesApi,
} from '../../api';
import PublicMenuExperience from '../../components/menu/PublicMenuExperience';
import { useAlerts } from '../../components/feedback/AlertProvider';
import { useLanguage } from '../../contexts/LanguageContext';
import translateApiError from '../../utils/translateApiError';
import '../../styles/Menus/ai-menu-designer.css';

const DEFAULT_ESTABLISHMENT_ID = 1;

const toArray = (value) => {
  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.data)) return value.data;

  if (value && typeof value === 'object') {
    return Object.values(value);
  }

  return [];
};

function AiMenuDesigner() {
  const { menuId } = useParams();
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const { showAlert } = useAlerts();

  const establishmentId =
    localStorage.getItem('selected_establishment_id') ||
    DEFAULT_ESTABLISHMENT_ID;

  const [menu, setMenu] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [generation, setGeneration] = useState(null);
  const [direction, setDirection] = useState('');
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [accepting, setAccepting] = useState(false);
  const [error, setError] = useState('');

  // Load the existing menu and the same preview payload used by customization.
  const loadPage = useCallback(async () => {
    try {
      setLoading(true);
      setError('');

      const [menuResponse, previewResponse] = await Promise.all([
        menusApi.getMenu(menuId),
        menuTemplatesApi.getMenuPreview(
          establishmentId,
          menuId,
        ),
      ]);

      setMenu(menuResponse.data?.data || null);
      setPreviewData(previewResponse.data?.data || null);
    } catch (requestError) {
      const message = translateApiError(requestError, t);

      setError(message);
      showAlert({
        type: 'error',
        title: t('alerts.error'),
        message,
      });
    } finally {
      setLoading(false);
    }
  }, [establishmentId, menuId, showAlert, t]);

  useEffect(() => {
    loadPage();
  }, [loadPage]);

  const categories = useMemo(
    () =>
      toArray(
        previewData?.categories ||
          menu?.categories ||
          [],
      ),
    [menu, previewData],
  );

  const products = useMemo(
    () =>
      toArray(
        previewData?.products ||
          menu?.products ||
          [],
      ),
    [menu, previewData],
  );

  const featuredProducts = useMemo(
    () =>
      toArray(
        previewData?.featured_products ||
          products.filter(
            (product) =>
              product?.is_featured ||
              product?.pivot?.is_featured,
          ),
      ),
    [previewData, products],
  );

  const establishment =
    previewData?.establishment ||
    previewData?.restaurant ||
    menu?.establishment ||
    null;

  const effectiveConfig =
    generation?.config ||
    menu?.design_settings ||
    previewData?.menu?.design_settings ||
    {};

  const activeTemplate =
    generation
      ? {
          id: `ai-${generation.id}`,
          name: t('menus.aiDesigner.generatedTemplate'),
          config: generation.config || {},
        }
      : previewData?.template ||
        menu?.template ||
        null;

  // Ask the backend AI designer for a first or alternative visual config.
  const generateDesign = async () => {
    if (!menu?.id || generating) return;

    try {
      setGenerating(true);
      setError('');

      const response = await aiMenuDesignApi.generate(
        menu.id,
        {
          previous_generation_id: generation?.id || null,
          direction: direction.trim() || null,
        },
      );

      const nextGeneration = response.data?.data || null;

      setGeneration(nextGeneration);
      setDirection('');

      showAlert({
        type: 'success',
        title: t('alerts.success'),
        message: t('menus.aiDesigner.generatedSuccess'),
      });
    } catch (requestError) {
      const message = translateApiError(requestError, t);

      setError(message);
      showAlert({
        type: 'error',
        title: t('alerts.error'),
        message,
      });
    } finally {
      setGenerating(false);
    }
  };

  // Accept the current config and continue to the normal manual customizer.
  const acceptDesign = async () => {
    if (!menu?.id || !generation?.id || accepting) {
      return;
    }

    try {
      setAccepting(true);

      await aiMenuDesignApi.accept(
        menu.id,
        generation.id,
      );

      showAlert({
        type: 'success',
        title: t('alerts.success'),
        message: t('menus.aiDesigner.acceptedSuccess'),
      });

      navigate(`/menus/${menu.id}/customize`);
    } catch (requestError) {
      const message = translateApiError(requestError, t);

      showAlert({
        type: 'error',
        title: t('alerts.error'),
        message,
      });
    } finally {
      setAccepting(false);
    }
  };

  if (loading) {
    return (
      <main className="ai-menu-designer ai-menu-designer--state">
        <Loader2 className="ai-menu-designer__spinner" size={30} />
        <p>{t('menus.aiDesigner.loading')}</p>
      </main>
    );
  }

  if (error && !menu) {
    return (
      <main className="ai-menu-designer ai-menu-designer--state">
        <p>{error}</p>
        <Link
          to="/menus"
          className="ai-menu-designer__secondary-button"
        >
          <ArrowLeft size={17} />
          {t('menus.aiDesigner.backToMenus')}
        </Link>
      </main>
    );
  }

  return (
    <main className="ai-menu-designer">
      <header className="ai-menu-designer__header">
        <div>
          <Link
            to={`/menus/${menuId}`}
            className="ai-menu-designer__back"
          >
            <ArrowLeft size={17} />
            {t('menus.aiDesigner.back')}
          </Link>

          <div className="ai-menu-designer__eyebrow">
            <Sparkles size={15} />
            {t('menus.aiDesigner.eyebrow')}
          </div>

          <h1>{t('menus.aiDesigner.title')}</h1>
          <p>{t('menus.aiDesigner.description')}</p>
        </div>
      </header>

      <section className="ai-menu-designer__workspace">
        <aside className="ai-menu-designer__panel">
          <div className="ai-menu-designer__menu-chip">
            <span>
              <WandSparkles size={19} />
            </span>

            <div>
              <small>{t('menus.aiDesigner.designing')}</small>
              <strong>{menu?.name}</strong>
            </div>
          </div>

          {!generation ? (
            <div className="ai-menu-designer__intro">
              <span className="ai-menu-designer__hero-icon">
                <Sparkles size={28} />
              </span>

              <h2>{t('menus.aiDesigner.readyTitle')}</h2>
              <p>{t('menus.aiDesigner.readyDescription')}</p>

              <div className="ai-menu-designer__rules">
                <span>
                  <Check size={15} />
                  {t('menus.aiDesigner.ruleKeepProducts')}
                </span>
                <span>
                  <Check size={15} />
                  {t('menus.aiDesigner.ruleKeepPrices')}
                </span>
                <span>
                  <Check size={15} />
                  {t('menus.aiDesigner.ruleDesignOnly')}
                </span>
              </div>

              <button
                type="button"
                className="ai-menu-designer__primary-button"
                onClick={generateDesign}
                disabled={generating}
              >
                {generating ? (
                  <Loader2 size={18} />
                ) : (
                  <Sparkles size={18} />
                )}

                {generating
                  ? t('menus.aiDesigner.generating')
                  : t('menus.aiDesigner.generate')}
              </button>
            </div>
          ) : (
            <>
              <div className="ai-menu-designer__proposal">
                <small>
                  {t('menus.aiDesigner.proposal')} #
                  {generation.generation_number}
                </small>

                <h2>
                  {t('menus.aiDesigner.generatedTitle')}
                </h2>

                <p>
                  {generation.style_reason ||
                    t('menus.aiDesigner.generatedDescription')}
                </p>

                <div className="ai-menu-designer__summary-grid">
                  <span>
                    <small>{t('menus.aiDesigner.cardStyle')}</small>
                    <strong>{generation.config?.card_style}</strong>
                  </span>
                  <span>
                    <small>{t('menus.aiDesigner.header')}</small>
                    <strong>{generation.config?.header_layout}</strong>
                  </span>
                  <span>
                    <small>{t('menus.aiDesigner.categories')}</small>
                    <strong>{generation.config?.category_style}</strong>
                  </span>
                  <span>
                    <small>{t('menus.aiDesigner.density')}</small>
                    <strong>{generation.config?.product_density}</strong>
                  </span>
                </div>
              </div>

              <label className="ai-menu-designer__direction">
                <span>{t('menus.aiDesigner.directionLabel')}</span>

                <textarea
                  value={direction}
                  onChange={(event) =>
                    setDirection(event.target.value)
                  }
                  maxLength={500}
                  placeholder={t(
                    'menus.aiDesigner.directionPlaceholder',
                  )}
                />
              </label>

              <div className="ai-menu-designer__actions">
                <button
                  type="button"
                  className="ai-menu-designer__secondary-button"
                  onClick={generateDesign}
                  disabled={generating || accepting}
                >
                  {generating ? (
                    <Loader2 size={17} />
                  ) : (
                    <RefreshCcw size={17} />
                  )}

                  {t('menus.aiDesigner.generateAnother')}
                </button>

                <button
                  type="button"
                  className="ai-menu-designer__primary-button"
                  onClick={acceptDesign}
                  disabled={generating || accepting}
                >
                  {accepting ? (
                    <Loader2 size={17} />
                  ) : (
                    <Check size={17} />
                  )}

                  {accepting
                    ? t('menus.aiDesigner.accepting')
                    : t('menus.aiDesigner.accept')}
                </button>
              </div>

              <div className="ai-menu-designer__hint">
                <Send size={14} />
                <span>{t('menus.aiDesigner.hint')}</span>
              </div>
            </>
          )}
        </aside>

        <section className="ai-menu-designer__preview">
          <div className="ai-menu-designer__preview-header">
            <div>
              <small>{t('menus.aiDesigner.previewEyebrow')}</small>
              <strong>{t('menus.aiDesigner.previewTitle')}</strong>
            </div>

            {generation && (
              <span className="ai-menu-designer__live-badge">
                <Sparkles size={13} />
                {t('menus.aiDesigner.aiPreview')}
              </span>
            )}
          </div>

          <div className="ai-menu-designer__phone-stage">
            <div className="ai-menu-designer__phone">
              <PublicMenuExperience
                mode="preview"
                phonePreview
                menu={{
                  ...menu,
                  ...(previewData?.menu || {}),
                  design_settings: effectiveConfig,
                }}
                establishment={establishment}
                categories={categories}
                products={products}
                featuredProducts={featuredProducts}
                template={activeTemplate}
                designSettings={effectiveConfig}
                orderingEnabled={
                  previewData?.ordering_enabled ??
                  menu?.ordering_enabled ??
                  false
                }
                language={language}
                className="ai-menu-designer__live-menu"
              />
            </div>

            {generating && (
              <div className="ai-menu-designer__generating-overlay">
                <span>
                  <Loader2 size={28} />
                </span>
                <strong>
                  {t('menus.aiDesigner.generatingTitle')}
                </strong>
                <p>
                  {t('menus.aiDesigner.generatingDescription')}
                </p>
              </div>
            )}
          </div>
        </section>
      </section>
    </main>
  );
}

export default AiMenuDesigner;